import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, item_count: 0 });
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);

  // Initialize session ID
  useEffect(() => {
    let storedSessionId = localStorage.getItem('cushion_session_id');
    if (!storedSessionId) {
      storedSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('cushion_session_id', storedSessionId);
    }
    setSessionId(storedSessionId);
  }, []);

  // Fetch cart when session ID is available
  const fetchCart = useCallback(async () => {
    if (!sessionId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (item) => {
    try {
      setLoading(true);
      await axios.post(`${API}/cart/add`, {
        session_id: sessionId,
        ...item
      });
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      await axios.delete(`${API}/cart/${sessionId}/item/${itemId}`);
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API}/cart/${sessionId}`);
      setCart({ items: [], subtotal: 0, item_count: 0 });
    } catch (error) {
      console.error('Error clearing cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateItemQuantity = async (itemId, quantity) => {
    // Optimistic update - instantly update local state
    setCart(prev => {
      const items = prev.items.map(item => {
        if (item.id === itemId) {
          const newTotal = round2(item.unit_price * quantity);
          return { ...item, quantity, total_price: newTotal };
        }
        return item;
      });
      const subtotal = round2(items.reduce((sum, i) => sum + (i.total_price || 0), 0));
      return { ...prev, items, subtotal };
    });
    // Sync to backend in background
    try {
      await axios.patch(`${API}/cart/${sessionId}/item/${itemId}/quantity?quantity=${quantity}`);
    } catch (error) {
      console.error('Error updating quantity:', error);
      await fetchCart(); // Revert on error
    }
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  const value = {
    cart,
    sessionId,
    loading,
    addToCart,
    removeFromCart,
    updateItemQuantity,
    clearCart,
    fetchCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
